import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import DatePicker from '@rio-cloud/rio-uikit/DatePicker';
import Select, { SelectOption } from '@rio-cloud/rio-uikit/Select';
import classNames from 'classnames';
import { Moment } from 'moment/moment';
import React, { useId } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { LabelWithTimezone } from '../LabelWithTimezone';
import { OptionalLabel } from '../OptionalLabel';

interface FilterModalButtonProps {
    isFilterActive: boolean;
    setShowFilterModal: (value: boolean) => void;
}

export const FilterModalButton = (props: FilterModalButtonProps) => {
    const handleOnClickFilter = () => props.setShowFilterModal(true);

    return (
        <button
            className={classNames(props.isFilterActive ? 'btn btn-primary' : 'btn btn-default')}
            onClick={handleOnClickFilter}
        >
            <span
                className={classNames(
                    props.isFilterActive ? 'rioglyph rioglyph-filter-active' : 'rioglyph rioglyph-filter',
                )}
            />
            <FormattedMessage id="outboundOrderBook.common.table.toolbar.filter" />
        </button>
    );
};

interface FilterModalFooterProps {
    isApplyButtonDisabled: boolean;
    handleOnClickApply: () => void;
    handleOnClickCancel: () => void;
    resetFilters: () => void;
}

export const FilterModalFooter = (props: FilterModalFooterProps) => {
    return (
        <>
            <button type="button" className="btn btn-primary btn-link" onClick={props.resetFilters}>
                <span className="rioglyph rioglyph-revert" aria-hidden="true" />
                <FormattedMessage id="outboundOrderBook.common.table.toolbar.filter.reset" />
            </button>
            <ButtonToolbar>
                <button className="btn btn-default" onClick={props.handleOnClickCancel}>
                    <FormattedMessage id="outboundPortal.cancel" />
                </button>
                <button
                    className="btn btn-primary"
                    onClick={props.handleOnClickApply}
                    disabled={props.isApplyButtonDisabled}
                >
                    <FormattedMessage id="outboundPortal.apply" />
                </button>
            </ButtonToolbar>
        </>
    );
};

interface FilterModalTextInputProps {
    id?: string;
    headerMessageId: string;
    isOptional?: boolean;
    inputAddon?: string;
    placeholderMessageId: string;
    value: string | undefined;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const FilterModalTextInput = (props: FilterModalTextInputProps) => {
    const intl = useIntl();
    const glyph = props.inputAddon || 'rioglyph rioglyph-search';

    return (
        <>
            <OptionalLabel isOptional={Boolean(props.isOptional)} htmlFor={props.id}>
                <FormattedMessage id={props.headerMessageId} />
            </OptionalLabel>
            <div className="input-group">
                <span className="input-group-addon">
                    <span className={glyph} />
                </span>
                <input
                    id={props.id}
                    className="form-control"
                    type="text"
                    placeholder={intl.formatMessage({ id: props.placeholderMessageId })}
                    value={props.value ?? ''}
                    onChange={props.onChange}
                    maxLength={35}
                />
            </div>
        </>
    );
};

interface FilterModalSelectProps {
    labelMessageId: string;
    inputAddon?: string | undefined;
    placeholderMessageId: string;
    isOptional?: boolean;
    options: SelectOption[];
    value: string | undefined;
    onChange: (item: SelectOption | undefined) => void;
}

export const FilterModalSelect = (props: FilterModalSelectProps) => {
    const id = useId();
    const intl = useIntl();

    return (
        <>
            <OptionalLabel isOptional={Boolean(props.isOptional)} htmlFor={id}>
                <FormattedMessage id={props.labelMessageId} />
            </OptionalLabel>
            <Select
                id={id}
                inputAddon={props.inputAddon}
                placeholder={intl.formatMessage({ id: props.placeholderMessageId })}
                options={props.options}
                value={props.value === undefined ? undefined : [props.value]}
                onChange={props.onChange}
                useClear
            />
        </>
    );
};

interface FilterModalDatePickerProps {
    id?: string;
    labelId: string;
    isOptional?: boolean;
    hasFormatError: boolean;
    hasTimeRangeOrderError: boolean;
    value: Date | undefined | string;
    onChange: (date: Moment | string | undefined) => void;
}

export const FilterModalDatePicker = (props: FilterModalDatePickerProps) => {
    const intl = useIntl();

    const inputProps: React.HTMLProps<HTMLInputElement> = {
        id: props.id,
        placeholder: intl.formatMessage({
            id: 'outboundPortal.common.chooseDatePlaceholder',
        }),
    };

    return (
        <>
            <LabelWithTimezone
                htmlFor={props.id}
                messageId={props.labelId}
                isOptional={props.isOptional}
                // eslint-disable-next-line react-hooks/purity
                timeZoneData={{ timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, date: Date.now() }}
            />
            <div
                className={classNames('form-group', {
                    'has-feedback has-error margin-bottom-25': props.hasFormatError || props.hasTimeRangeOrderError,
                })}
            >
                <DatePicker
                    locale={intl.locale}
                    mandatory={false}
                    inputProps={props.value === undefined ? { ...inputProps, value: '' } : inputProps}
                    value={props.value}
                    onChange={props.onChange}
                    className="margin-bottom-0"
                />
                {props.hasFormatError && (
                    <div>
                        <span className="form-control-feedback rioglyph rioglyph-error-sign" />
                        <span className="help-block">
                            <FormattedMessage id="outboundPortal.common.invalidDate" />
                        </span>
                    </div>
                )}
                {props.hasTimeRangeOrderError && (
                    <div>
                        <span className="form-control-feedback rioglyph rioglyph-error-sign" />
                        <span className="help-block">
                            <FormattedMessage id="outboundPortal.transportAssignments.table.toolbar.filter.timeRangeError" />
                        </span>
                    </div>
                )}
            </div>
        </>
    );
};
